import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { Registration, PaymentStatus } from "@/pages/hub/HubTournamentDemo";

type FilterValue = "all" | PaymentStatus;

interface Props {
  registrations: Registration[];
  onUpdatePayment: (id: number, paid: number, status: PaymentStatus) => void;
}

function StatusBadge({ status }: { status: PaymentStatus }) {
  if (status === "paid")
    return (
      <Badge className="bg-green-100 text-green-800 hover:bg-green-100 border-green-200 border">
        Paid
      </Badge>
    );
  if (status === "deposit")
    return (
      <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 border-yellow-200 border">
        Deposit Only
      </Badge>
    );
  return (
    <Badge className="bg-red-100 text-red-800 hover:bg-red-100 border-red-200 border">
      Unpaid
    </Badge>
  );
}

function UpdatePaymentDialog({
  registration,
  onUpdate,
}: {
  registration: Registration;
  onUpdate: (id: number, paid: number, status: PaymentStatus) => void;
}) {
  const [open, setOpen] = useState(false);
  const [paymentType, setPaymentType] = useState<"deposit" | "full">("full");
  const [amount, setAmount] = useState("");

  function handleConfirm() {
    const paidAmount = parseFloat(amount) || registration.amountDue;
    const newStatus: PaymentStatus =
      paidAmount >= registration.amountDue ? "paid" : "deposit";
    onUpdate(registration.id, paidAmount, newStatus);
    setOpen(false);
    setAmount("");
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="text-xs"
          data-testid={`btn-update-payment-${registration.id}`}
        >
          Update Payment
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>Update Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <div>
            <p className="text-sm font-medium text-gray-900">{registration.teamName}</p>
            <p className="text-xs text-gray-500">
              Due: ${registration.amountDue} · Paid so far: ${registration.paid}
            </p>
          </div>

          <div>
            <Label className="mb-2 block">Payment Type</Label>
            <RadioGroup
              value={paymentType}
              onValueChange={(v) => setPaymentType(v as "deposit" | "full")}
              className="flex gap-4"
            >
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="deposit" id="pay-deposit" />
                <Label htmlFor="pay-deposit" className="cursor-pointer font-normal">
                  Deposit
                </Label>
              </div>
              <div className="flex items-center gap-1.5">
                <RadioGroupItem value="full" id="pay-full" />
                <Label htmlFor="pay-full" className="cursor-pointer font-normal">
                  Full Payment
                </Label>
              </div>
            </RadioGroup>
          </div>

          <div>
            <Label htmlFor="pay-amount">Amount Received ($)</Label>
            <Input
              id="pay-amount"
              type="number"
              className="mt-1"
              placeholder={
                paymentType === "full"
                  ? String(registration.amountDue)
                  : String(registration.amountDue / 2)
              }
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          </div>

          <div>
            <Label className="text-xs text-gray-500">Zelle Screenshot (demo only)</Label>
            <Input type="file" accept="image/*" className="mt-1 text-xs" />
            <p className="text-xs text-gray-400 mt-1">
              File upload is visual only — not stored in this demo.
            </p>
          </div>

          <Button
            className="w-full bg-[#1E2761] hover:bg-[#2d3a8c] text-white"
            onClick={handleConfirm}
            data-testid={`btn-confirm-payment-${registration.id}`}
          >
            Confirm Payment
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function PaymentTrackingTab({ registrations, onUpdatePayment }: Props) {
  const [filter, setFilter] = useState<FilterValue>("all");

  const filtered =
    filter === "all" ? registrations : registrations.filter((r) => r.status === filter);

  const totalCollected = registrations.reduce((s, r) => s + r.paid, 0);
  const totalOutstanding = registrations.reduce((s, r) => s + (r.amountDue - r.paid), 0);

  return (
    <div className="space-y-4" data-testid="payment-tracking-tab">
      {/* Filter */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-base font-semibold text-gray-900">
          {registrations.length} teams registered
        </h2>
        <Select
          value={filter}
          onValueChange={(v) => setFilter(v as FilterValue)}
        >
          <SelectTrigger className="w-44" data-testid="filter-status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Teams</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="deposit">Deposit Only</SelectItem>
            <SelectItem value="unpaid">Unpaid</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filtered.length === 0 ? (
            <div className="py-16 text-center text-gray-400">
              <p className="text-sm">No teams match this filter.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="text-xs">Academy</TableHead>
                    <TableHead className="text-xs">Age</TableHead>
                    <TableHead className="text-xs">Team Name</TableHead>
                    <TableHead className="text-xs hidden md:table-cell">Coach</TableHead>
                    <TableHead className="text-xs text-right">Due</TableHead>
                    <TableHead className="text-xs text-right">Paid</TableHead>
                    <TableHead className="text-xs text-right">Balance</TableHead>
                    <TableHead className="text-xs">Status</TableHead>
                    <TableHead className="text-xs" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((r) => (
                    <TableRow key={r.id} data-testid={`payment-row-${r.id}`}>
                      <TableCell className="text-sm font-medium max-w-[120px] truncate">
                        {r.academy}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {r.ageGroup}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{r.teamName}</TableCell>
                      <TableCell className="text-sm text-gray-500 hidden md:table-cell">
                        {r.coachName}
                      </TableCell>
                      <TableCell className="text-sm text-right">${r.amountDue}</TableCell>
                      <TableCell className="text-sm text-right text-green-700 font-medium">
                        ${r.paid}
                      </TableCell>
                      <TableCell className="text-sm text-right text-red-600 font-medium">
                        ${r.amountDue - r.paid}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={r.status} />
                      </TableCell>
                      <TableCell>
                        <UpdatePaymentDialog
                          registration={r}
                          onUpdate={onUpdatePayment}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer summary */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-6 text-sm">
            <span>
              <span className="text-gray-500">Total Collected: </span>
              <span className="font-semibold text-green-700">
                ${totalCollected.toLocaleString()}
              </span>
            </span>
            <span>
              <span className="text-gray-500">Total Outstanding: </span>
              <span className="font-semibold text-red-600">
                ${totalOutstanding.toLocaleString()}
              </span>
            </span>
            <span>
              <span className="text-gray-500">Teams Paid in Full: </span>
              <span className="font-semibold">
                {registrations.filter((r) => r.status === "paid").length}
              </span>
            </span>
            <span>
              <span className="text-gray-500">Deposit Only: </span>
              <span className="font-semibold text-yellow-700">
                {registrations.filter((r) => r.status === "deposit").length}
              </span>
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
